/*
 * Copyright (C) Ivo Ladage-van Doorn - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Ivo Ladage-van Doorn <ivol37@gmail.com>, 2024
 */
import { participants, title } from "./utils";

import { InlineImage } from "../../components/InlineImage";
import { download } from "../../components/AssetLibrary";

type TrackRecording = {
  route: string;
  distance: number;
  avgSpeed: number | string;
  time: string;
  up: number;
  down: number;
  download: string | null;
};

function Doortrappers() {
  const stats = (distance: number, up: number, down: number, minHeight: number, maxHeight: number) => {
    return (
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Afstand</th>
              <th>Stijgen</th>
              <th>Dalen</th>
              <th>Min. hoogte</th>
              <th>Max. hoogte</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{distance} km</td>
              <td>+ {up} m</td>
              <td>- {down} m</td>
              <td>{minHeight} meter</td>
              <td>{maxHeight} meter</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const recording = (route: string, distance: number, avgSpeed: string | number, time: string, up: number, down: number, download: string | null) => {
    return {
      route: route,
      distance: distance,
      avgSpeed: avgSpeed,
      time: time,
      up: up,
      down: down,
      download: download,
    };
  };

  const recordings = (trackRecordings: TrackRecording[]) => {
    return (
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Traject</th>
              <th>Afstand</th>
              <th>Gem. snelheid</th>
              <th>Tijd</th>
              <th>Stijgen</th>
              <th>Dalen</th>
              <th>Download</th>
            </tr>
          </thead>
          <tbody>
            {trackRecordings.map((recording: TrackRecording, index: number) => {
              const extension = recording.download ? recording.download.substring(recording.download.lastIndexOf(".") + 1) : "";
              return (
                <tr key={index}>
                  <td>{recording.route}</td>
                  <td>{recording.distance} km</td>
                  <td>{recording.avgSpeed} km/h</td>
                  <td>{recording.time}</td>
                  <td>{recording.up} m</td>
                  <td>{recording.down} m</td>
                  <td>{recording.download ? download("doortrappers/recordings/" + recording.download, extension) : "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const gpxDownload = (file: string) => {
    return <p>{download("doortrappers/routes/" + file, "Download")} de route in GPX formaat</p>;
  };

  const toLeadImage = (image: string) => {
    return <InlineImage image={"lekkveld/doortrappers/" + image + ".jpg"} />;
  };

  return (
    <div>
      <div className="box light">
        <div className="row">
          <h1>De Doortrappers</h1>
          <p>
            In 2009 werd na lange tijd weer de traditie opgepakt om per fiets naar de Ardennen af te reizen. Dat was toen al enige tijd niet meer gebeurd vanwege het bezit van
            auto's en kinderen. In 2009 werd door een select gezelschap de heenreis per fiets afgelegd. Een nog kleiner gezelschap fietste ook de terugreis. In 2009 werd de afstand
            nog in 1 dag afgelegd. Vanaf 2010 werd het gebruikelijk om dat in 2 dagen te doen. In 2010 werd de naam "De Doortrappers" bedacht. Die naam kwam vooral tot stand
            doordat de trapper van Dennis dat jaar zich afsplitste van zijn fiets, na kilometers lang gekraakt te hebben.
          </p>
          <p>
            Hieronder staan overzichten van de gefietste afstanden. Omdat "platte kilometers eigenlijk niet tellen" staan ook het aantal meters genoemd wat er gestegen moest
            worden, en gedaald. Alle statistieken gelden alleen voor de heenreis. Omdat het startpunt doorgaans lager ligt dan het eindpunt, is dat tot nu altijd de zwaardere route
            geweest t.o.v. de terugweg.
          </p>

          <h2>Hoe maak je die kaartjes?</h2>
          <p>
            De hoogtekaartjes maakte ik voorheen m.b.v. Google Earth. Tegenwoordig gebruik ik <a href="https://web.locusmap.app">Locus Maps Web</a> en{"  0"}
            <a href="https://www.routeyou.com/">routeyou.com</a> waarin deze kaartjes na het uitzetten van de route direct zichtbaar zijn. Het is dus niet meer nodig deze te
            'maken'.
          </p>

          <h2>Hoogteprofielen</h2>
          <p>
            Om een indicatie te geven van hoe zwaar een bepaalde route is, bestaan er diverse methoden. Het aantal meters dat er op een route gestegen en gedaald moet worden is in
            grote mate bepalend voor hoe zwaar een route is, naast het aantal kilometers. Om een goede schatting van het aantal hoogtemeters te krijgen is echter lastig.
            Verschillende programma's gebruiken verschillende rekenmethodes en daardoor kunnen hoogtemeters van verschillende programma's flink van elkaar verschillen.
          </p>
          <p>
            Na wat onderzoek lijkt de rekenmethode van <a href="https://web.locusmap.app">Locus Maps Web</a> het meest betrouwbaar. De statistieken onder komen dan ook van dat
            programma.
          </p>
        </div>
      </div>
      <div className="box main">
        <div className="row">
          <h1>Overzicht alle fietstochten</h1>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Jaar</th>
                  <th>Van</th>
                  <th>Naar</th>
                  <th>Afstand</th>
                  <th>Stijgen</th>
                  <th>Dalen</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>2009</td>
                  <td>Roermond (NL)</td>
                  <td>Thimister-Clermont (BE)</td>
                  <td>85 km</td>
                  <td>+ 681 m</td>
                  <td>- 411 m</td>
                </tr>
                <tr>
                  <td>2010</td>
                  <td>Maastricht (NL)</td>
                  <td>Petites-Tailles (BE)</td>
                  <td>104 km</td>
                  <td>+ 1620 m</td>
                  <td>- 1065 m</td>
                </tr>
                <tr>
                  <td>2011</td>
                  <td>Troisponts (BE)</td>
                  <td>Vlessart (BE)</td>
                  <td>105 km</td>
                  <td>+ 1852 m</td>
                  <td>- 1650 m</td>
                </tr>
                <tr>
                  <td>2012</td>
                  <td>Roermond (NL)</td>
                  <td>Xhoffraix (BE)</td>
                  <td>147 km</td>
                  <td>+ 1335 m</td>
                  <td>- 823 m</td>
                </tr>
                <tr>
                  <td>2013</td>
                  <td>Winterswijk (NL)</td>
                  <td>Brilon (DE)</td>
                  <td>194 km</td>
                  <td>+ 1230 m</td>
                  <td>- 761 m</td>
                </tr>
                <tr>
                  <td>2014</td>
                  <td>Venlo (NL)</td>
                  <td>Niedersorpe (DE)</td>
                  <td>238 km</td>
                  <td>+ 2356 m</td>
                  <td>- 1936 m</td>
                </tr>
                <tr>
                  <td>2015</td>
                  <td>Eindhoven (NL)</td>
                  <td>Ovifat (BE)</td>
                  <td>204 km</td>
                  <td>+ 1553 m</td>
                  <td>- 985 m</td>
                </tr>
                <tr>
                  <td>2017</td>
                  <td>Tilburg (NL)</td>
                  <td>Gembloux (BE)</td>
                  <td>149 km</td>
                  <td>+ 1245 m</td>
                  <td>- 1147 m</td>
                </tr>
                <tr>
                  <td>2019</td>
                  <td>Nijmegen (NL)</td>
                  <td>Gemmenich (BE)</td>
                  <td>153 km</td>
                  <td>+ 659 m</td>
                  <td>- 475 m</td>
                </tr>
                <tr>
                  <td>2020</td>
                  <td>Ede (NL)</td>
                  <td>Pingjum (NL)</td>
                  <td>171 km</td>
                  <td>+ 126 m</td>
                  <td>- 150 m</td>
                </tr>
                <tr>
                  <td>2021</td>
                  <td>Goor (NL)</td>
                  <td>Mander (NL)</td>
                  <td>38 km</td>
                  <td>+ 64 m</td>
                  <td>- 41 m</td>
                </tr>
                <tr>
                  <td>2022</td>
                  <td>Nijmegen (NL)</td>
                  <td>Gronau (NL)</td>
                  <td>140 km</td>
                  <td>+ 457 m</td>
                  <td>- 430 m</td>
                </tr>
                <tr>
                  <td>2024</td>
                  <td>Nijmegen (NL)</td>
                  <td>Érezée (BE)</td>
                  <td>200 km</td>
                  <td>+ 1385 m</td>
                  <td>- 1113 m</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* --- 2009 --- */}
      <div className="box light">
        <div className="left">
          {title(2009, "Roermond (NL) - Thimister-Clermont (BE)")}
          {participants(4, "Paul K, Onze Henk, Ivo en Rianne")}
          <p>
            Omdat Stokis (naam van het huisje en de straat waar het aan ligt, in Thimister-Clermont) in het noorden van Belgi&euml; ligt werd er in Roermond al uit de trein gestapt
            om vanaf daar de rest te fietsen. Er was de nodige fietspech; de fiets van Ivo trapte door en dat werd steeds erger totdat ieder heuveltje alleen nog te voet genomen
            kon worden. De fiets werd omgeruild voor de racefiets van Tineke en het traject kon alsnog afgerond worden.
          </p>
          <p>
            Op de terugweg, nadat er al een lekke band geplakt was, was er nogmaals een lekke band. Ditmaal bleek echter dat het ventiel afgebroken was. En zo moest Elise
            opgetrommeld worden om de tegensputterende fiets inclusief berijder op te halen.
          </p>
        </div>

        <div className="right">{toLeadImage("2009_doortrappers")}</div>

        <div className="subbox">
          <div className="row">
            <h2>Statistieken</h2>
            {stats(85, 681, 411, 20, 326)}
            {toLeadImage("2009_hoogteprofiel")}
            {gpxDownload("2009-roermond-thimister-clermont.gpx")}
          </div>
        </div>
      </div>

      {/* --- 2010 --- */}
      <div className="box main">
        <div className="left">
          {title(2010, "Maastricht (NL) - Petites-Tailles (BE)")}
          {participants(6, "Paul K, Lenny, Dennis, Onze Henk, Ivo en Rianne")}
          <p>
            Omdat vanuit Nederland naar Petites-Tailles (in de buurt van Vielsalm) fietsen niet in 1 dag te doen is, werd dit jaar besloten de route in twee delen te fietsen en
            halverwege te overnachten. Er werd een leuke Bed & Breakfast gevonden in Verviers waar we zowel de heenweg als terugweg konden vertoeven. De tocht was beduidend
            zwaarder dan vorig jaar en menig fietser voelde de benen aanzienlijk toen we bij het huisje arriveerden.
          </p>
          <p>
            Ook dit jaar was er weer de nodige fietspech. Halverwege de heenweg besloot de trapper van Dennis zich af te splitsen van de rest van zijn fiets. Op de terugweg zijn
            het de remmen van diezelfde fiets die het begeven, deze werd uiteindelijk pas in Verviers gerepareerd.
          </p>
        </div>

        <div className="right">{toLeadImage("2010_doortrappers")}</div>

        <div className="subbox">
          <div className="row">
            <h2>Statistieken</h2>
            {stats(104, 1620, 1065, 46, 614)}
            {toLeadImage("2010_hoogteprofiel")}
            {gpxDownload("2010-maastricht-petites-tailles.gpx")}
          </div>
        </div>
      </div>

      {/* --- 2011 --- */}
      <div className="box light">
        <div className="left">
          {title(2011, "Troisponts (BE) - Vlessart (BE)")}
          {participants(5, "Paul K, Lenny, Dennis, Onze Henk en Ivo")}
          <p>
            Ook dit jaar werd er gekozen voor een 2-daagse tocht. Omdat Vlessart diep in Belgi&euml; ligt gingen we dit keer eerst met de trein helemaal naar Trois-Ponts. Vanuit
            daar fietsten we met een kleine omweg via Troisvierges naar Vlessart. In Troisvierges overnachtten we in een goedkoop, maar uitstekend, hotel.
          </p>
          <p>
            Dit maal was "fietsertje 3" (Rianne) er niet bij. Zij komt rechtstreeks vanuit Frankrijk naar Vlessart gereden. Op de terugweg haakte ook Ivo af, omdat zowel Elise als
            Mathijs zich niet helemaal jofel voelden.
          </p>
        </div>

        <div className="right">{toLeadImage("2011_doortrappers")}</div>

        <div className="subbox">
          <div className="row">
            <h2>Statistieken</h2>
            {stats(105, 1852, 1650, 250, 509)}
            {toLeadImage("2011_hoogteprofiel")}
            {gpxDownload("2011-trois-ponts-vlessart.gpx")}
          </div>
        </div>
      </div>

      {/* --- 2012 --- */}
      <div className="box main">
        <div className="left">
          {title(2012, "Roermond (NL) - Xhoffraix (BE)")}
          {participants(6, "Paul K, Lenny, Aldert, Dennis, Onze Henk en Ivo")}
          <p>
            Dit jaar verkozen we de loop van Ruhr te volgen, helemaal tot aan de oorsprong in Belgi&euml;. De eerste dag op de heenweg was zwaar, tenminste, voor Ivo die onderweg
            even de bosjes moest opzoeken om overtollige maaginhoud te lozen. Bij de aankomst bij het pension bleek de reservering niet serieus genomen te zijn, maar kon met wat
            improvisatie toch een slaapplaats ingericht worden.
          </p>
          <p>
            De volgende dag verliep voorspoedig, tot ongeveer 5 km voor de finish. Paul K nam het bospad met grote stenen met een iets te ambitieuze snelheid, waardoor zijn band de
            geest gaf. Ivo verloor op hetzelfde pad een fietstas die het hobbelige parcours eveneens niet aan kon. Tijdens het (tevergeefs) plakken van Pauls band begon het
            vervolgens te stortregenen (zie foto), zodat de doortrappers als verzopen katten bij het huisje aankwamen.
          </p>
          <p>De terugweg daarentegen verliep soepel en zonder tegenslagen.</p>
        </div>

        <div className="right">{toLeadImage("2012_doortrappers")}</div>

        <div className="subbox">
          <div className="row">
            <h2>Statistieken</h2>
            {stats(147, 1335, 823, 18, 617)}
            {toLeadImage("2012_hoogteprofiel")}
            {gpxDownload("2012-roermond-xhoffraix.gpx")}
          </div>
        </div>
      </div>

      {/* --- 2013 --- */}
      <div className="box light">
        <div className="left">
          {title(2013, "Winterswijk (NL) - Brilon (DE)")}
          {participants(4, "Paul K, Dennis, Onze Henk en Ivo")}
          <p>
            De foto hiernaast toont veel gelijkenissen met de foto van het jaar ervoor. Waar de foto van vorig jaar nog <b>tijdens</b>
            de bui genomen werd, werd deze foto <b>na</b> de bui genomen. Dit was niet zomaar een 'hoosbuitje' of zo maar een 'wolkbreukje', het was alsof we midden in de
            Niagara-watervallen stonden. Schuilen in het bos (zoals het jaar ervoor) had geen zin. Het dichtbegroeide bos kon deze idiote hoeveelheid water gewoon niet langer dan
            een paar minuten bolwerken.
          </p>
          <p>
            De sokken werden uitgewrongen terwijl de ene na de andere horzel verpletterd moest worden, die hadden wel trek in een doorweekt Doortrappertje. Zelfs menig kledingstuk
            dat goed verpakt zat in een fietstas met speciale regenhoes, bleek toch nat geworden te zijn.
          </p>
          <p>
            Op de terugweg werden we met een nieuwe extremiteit geconfronteerd: de temperatuur was gestegen tot een ruime 38 graden. Zolang je bleef fietsen was het nog te harden,
            maar stil staan na een eindje fietsen was geen goed idee. Maar ja, midden in een stad waar het stoplicht op rood staat heb je weinig keus...
          </p>
        </div>

        <div className="right">{toLeadImage("2013_doortrappers")}</div>

        <div className="subbox">
          <div className="row">
            <h2>Statistieken</h2>
            <h3>Geplande route</h3>
            {stats(194, 1230, 761, 33, 568)}
            {toLeadImage("2013_hoogteprofiel")}
            {gpxDownload("2013-winterswijk-brilon.gpx")}

            <h3>Gemeten route</h3>
            {recordings([
              recording("Winterswijk - Hamm", 95.2, 18.5, "5h:09m", 849, 854, "2013-07-25_Winterswijk-Hamm.kml"),
              recording("Hamm - Brilon", 85.7, 17.4, "4h:55m", 991, 566, "2013-07-25_Winterswijk-Hamm.kml"),
            ])}
          </div>
        </div>
      </div>

      {/* --- 2014 --- */}
      <div className="box main">
        <div className="left">
          {title(2014, "Venlo (NL) - Niedersorpe (DE)")}
          {participants(6, "Paul K (Atari-Meister), Dennis (Schwitzmeister), Onze Henk (Pantalonmeister), Ivo (terug) (Fahrradführer), Aldert (heen) en Bokkie (Superbok)")}
          <p>
            Op de heenweg was ik er zelf niet bij, maar naar verluidt was het een draconische rit. Op de eerste dag arriveerden de Doortrappers om 22:30 bij de Bed & Breakfast,
            waardoor de doortrappers geen warme maaltijd meer konden genieten. Het avondmaal was volgens de overlevering beperkt tot{" "}
            <i>"een koude gehaktbal met saus uit een fles met een dop die niet was schoongemaakt"</i>. Ook de tweede dag eindigde in het donker. De Doortrappers arriveerden rond
            21:30, en gelukkig kon er toen nog wel een maaltijd opgewarmd worden. Nog niet eerder moest er in het donker gefietst worden, maar deze heenreis twee maal.
          </p>
          <p>
            Op de terugweg was er weer de nodige regen. Niet van die heftige buien, maar wel veel regen. Bokkie zag er in zijn poncho meer uit als een blauwe smurf. Maar beter dat
            dan Onze Henk, die helemaal geen regenkleding bij zich had. En zelfs de pantalon van Onze Henk is daar niet tegen opgewassen.
          </p>
          <p>De bijnamen beginnen ingeburgerd te raken, dus worden ze er vanaf nu (soms) bij vermeld.</p>
        </div>

        <div className="right">{toLeadImage("2014_doortrappers")}</div>

        <div className="subbox">
          <div className="row">
            <h2>Statistieken</h2>

            <h3>Geplande route</h3>
            {stats(238, 2356, 1936, 43, 490)}
            {toLeadImage("2014_hoogteprofiel")}
            {gpxDownload("2014-venlo-niedersorpe.gpx")}

            <h3>Gemeten route</h3>
            {recordings([
              recording("Niedersorpe - Sprockhovel", 134, 17.7, "7h:32m", 1563, 1756, "2014-08-15_Niedersorpe-Sprockhovel.kml"),
              recording("Sprockhovel - Venlo", 96.6, 16.8, "5h:44m", 908, 1054, "2014-08-16_Sprockhovel-Venl.kml"),
            ])}
          </div>
        </div>
      </div>

      {/* --- 2015 --- */}
      <div className="box light">
        <div className="left">
          {title(2015, "Eindhoven (NL) - Ovifat (BE)")}
          {participants(
            5,
            "Paul K (Maître d'Atari), Dennis (L'Empereur du Transpiration), Onze Henk (Le Roi de Pantalon), Ivo (terug) (Chef de Mission) en Bokkie (General Bokkie)"
          )}
          <p>
            Dit jaar werden veel bijzondere punten aangedaan tijdens de reis; het hoogste punt van Nederland (Vaalserberg), het hoogste punt van Belgi&euml; (Signal de Botrange),
            het zuidelijkste punt van Nederland, het drielandenpunt en natuurlijk de beroemde Cauberg. De Cauberg bleek kort, maar bijzonder stijl. Met volle bepakking, 100
            kilometer in de benen en een gewone stadsfiets is dat behoorlijk aanpoten.
          </p>
          <p>
            Ondanks de grote problemen, die zich ieder jaar maar verder opstapelen, werd de eindstreep op zowel de heen- als terugweg gehaald. Ondanks diverse lekke banden, een
            afgesloten brug en natuurlijk het bovenste deel van een Romeinse fallus.
          </p>
        </div>

        <div className="right">
          {toLeadImage("2015_doortrappers")} {toLeadImage("2015_doortrappers_2")}
        </div>

        <div className="subbox">
          <div className="row">
            <h2>Statistieken</h2>

            <h3>Geplande route</h3>
            {stats(204, 1553, 985, 16, 589)}
            {toLeadImage("2015_hoogteprofiel")}
            {gpxDownload("2015-eindhoven-ovifat.gpx")}

            <h3>Gemeten route</h3>
            {recordings([
              recording("Eindhoven - Heijenrath", 115, "?", "?", 1000, 798, null),
              recording("Heijenrath - Ovifat", 88.9, 17.5, "5h:03m", 1078, 705, "2015-07-31_Heijenrath-Ovifat.kml"),
            ])}
          </div>
        </div>
      </div>

      {/* --- 2017 --- */}
      <div className="box main">
        <div className="left">
          {title(2017, "Tilburg (NL) - Gembloux (BE)")}
          {participants(5, "Paul K (Atarimeister), Dennis (Schwitzi), Onze Henk (Pantalonmeister), Ivo (Fahradfuhrer) en Aldert (De Beul van Utrecht)")}
          <p>
            Het was de fietstocht waarbij <i>De Beul van Utrecht</i> toesloeg. Op de eerste dag lag het gemiddelde tempo met 18,9 km/h al hoog. De tweede dag was het tempo
            onverminderd hoog, maar doordat we ervoor kozen door het centrum van Leuven te fietsen op de "Belgische Koninginnedag" vertekenen de statistieken van die dag. De eerste
            dag op de terugweg ging met 19,9 km/h, de laatste dag zelfs met 21,7 km/h (14,2 km/h inclusief rust en lunch). Dat gemiddelde haalden we op een vlakke rit niet eerder;
            de Beul kan tevreden terug kijken.
          </p>
        </div>

        <div className="right">
          {toLeadImage("2017_doortrappers")} {toLeadImage("2017_doortrappers_2")}
        </div>

        <div className="subbox">
          <div className="row">
            <h2>Statistieken</h2>

            <h3>Geplande route</h3>
            {stats(149, 1245, 1147, 0, 180)}
            {toLeadImage("2017_hoogteprofiel")}
            {gpxDownload("2017-tilburg-gembloux.gpx")}

            <h3>Gemeten route</h3>
            {recordings([
              recording("Tilburg - Houtvenne", 70, 18.9, "3h:43m", 662, 701, "2017-07-20_Tilburg-Houtvenne.kml"),
              recording("Houtvenne - Gembloux", 78, 17.0, "4h:34m", 1004, 846, "2017-07-21_Houtvenne-Gembloux.kml"),
            ])}
          </div>
        </div>
      </div>

      {/* --- 2019 --- */}
      <div className="box light">
        <div className="left">
          {title(2019, "Nijmegen (NL) - Gemmenich (BE)")}
          {participants(5, "Paul K, Onze Henk, Ivo, Rianne en Inge")}
          <p>
            Na jaren van afwezigheid nam het kleinste doortrappertje weer deel aan deze fietstocht. Ook werd een nieuwe doortrapper aan het assortiment toegevoegd; Inge. De tocht
            voerde vanuit Nijmegen naar Gemmenich. Halverwege in het pittoreske Roggel werd overnacht, bij een uitermate leuk B&B gerund door een vrouw met liefde voor koken. De
            maaltijd was dan ook voortreffelijk verzorgd, alleen 'een bietje veel' (foto linksboven). Aan ruimte was er geen gebrek op de slaapkamers.
          </p>
          <p>
            De tocht verliep wonder boven wonder zonder noemenswaardige problemen. Zelfs geen lekke band. Alleen pakten rondom Heerlen donkere wolken zich samen, en liep dat uit op
            een forse regenbui. Er werd geschuild in voortuintjes van lokale bewoners, waarna de weg vervolgd werd. Het bleef oppassen, maar een groot waterspektakel bleef uit.
          </p>
          <p>
            {" "}
            Op de terugweg moest buienradar nogmaals nauwkeurig in de gaten gehouden worden. Buienradar bleek toch niet zo nauwkeurig als altijd gedacht, maar gelukkig bood een
            bushokje de benodigde beschutting. De terugweg verliep wederom via de B&B in Roggel, waar de maaltijd weer 'stevig' was. Op de terugweg viel het oog op de "Kaasweg", op
            een blauw bordje die ook nog eens voorzien was van schimmel; de Blauwe Schimmelkaasweg dus! Dat moest op de foto; de foto linksonder.
          </p>
          <p>Op de terugweg was er de nodige zijwind en tegenwind voor de Ossenaren, maar uiteindelijk was iedereen ruim op tijd voor het avondeten weer thuis.</p>
        </div>

        <div className="right">
          {toLeadImage("2019_doortrappers")} {toLeadImage("2019_doortrappers_2")}
        </div>

        <div className="subbox">
          <div className="row">
            <h2>Statistieken</h2>

            <h3>Geplande route</h3>
            {stats(153, 659, 475, 9, 282)}
            {toLeadImage("2019_hoogteprofiel")}
            {gpxDownload("2019-nijmegen-gemmenich.gpx")}

            <h3>Gemeten route</h3>
            {recordings([
              recording("Nijmegen - Gemmenich", 154.6, 17.5, "8h:49m", 1678, 1509, "2019-08-01_Nijmegen-Gemmenich.kml"),
              recording("Gemmenich - Nijmegen", 152.8, 20.1, "7h:36m", 1296, 1470, "2019-08-09_Gemmenich-Nijmegen.kml"),
            ])}
          </div>
        </div>
      </div>

      {/* --- 2020 --- */}
      <div className="box main">
        <div className="left">
          {title(2020, "Ede (NL) - Pingjum (NL)")}
          {participants(6, "Paul K, Dennis, Onze Henk, Ivo, Bokkie en Inge")}
          <p>
            Dit jaar werd er gestart in Ede. De route voer via het pittoreske Otterlo, met een grote boog om Harskamp heen, naar het Noorden. Er werd overnacht en gedineerd in
            hotel Saaze in Kraggenburg. Het was een bloedhete nacht, waarbij bleek dat het hotel niet over airco beschikte. Menig doortrapper kon de slaap dan ook niet vatten.
          </p>
          <p>
            De tweede dag kenmerkte zich door smalle paadjes door smalle steegjes en over smalle bruggetjes. Er werd gemopperd over Ivo-paadjes, maar dat viel natuurlijk allemaal
            reuze mee.
          </p>
          <p>
            Op de terugweg splitste de groep zich in tweeën; een groep die in 1 dag naar huis fietste (met hulp van de trein) en een groep die daar 2 dagen over deed. De 1-dags
            groep nam wel een kleine omweg via Vollenhove. Aldaar konden zij aanschuiven bij Gea en Henk Alle, die toen nog in Vollenhove woonden. De pannenkoeken werden door de
            Doortrappers gulzig naar binnen geschoven. De andere groep overnachtte wederom in hotel Saaze.
          </p>
        </div>

        <div className="right">
          {toLeadImage("2020_doortrappers")} {toLeadImage("2020_doortrappers_2")}
        </div>

        <div className="subbox">
          <div className="row">
            <h2>Statistieken</h2>

            <h3>Geplande route</h3>
            {stats(171, 126, 150, -5, 44)}
            {toLeadImage("2020_hoogteprofiel")}
            {gpxDownload("2020-ede-pingjum.gpx")}
          </div>
        </div>
      </div>

      {/* --- 2021 --- */}
      <div className="box light">
        <div className="left">
          {title(2021, "Goor (NL) - Mander (NL)")}
          {participants(6, "Paul K (Atarimeister), Dennis (Schwitzi), Onze Henk (Pantalonmeister), Ivo (Fahrradführer), Lenny/Aldert en Mathijs (Frietmeister)")}
          <p>
            Dit jaar was er de verwelkoming van weer een nieuwe Doortrapper; Mathijs alias 'Frietmeister'. Hij was op dat moment 10 jaar oud, en dus kon de route niet al te lang
            zijn. De groep splitste in tweeën waarbij 1 groep startte in Deventer (iedereen behalve Ivo en Mathijs) en een groep in Goor (Ivo en Mathijs). Voor beide groepen was
            het dit keer een fietstocht van 1 dag.
          </p>
          <p>
            Op de heenweg fietste Lenny mee, op de terugweg Aldert. Op de route lag de 'Holterberg' wat zowaar een klein klimmetje opleverde. Bovenop de Holterberg werd op de
            terugweg nog het Natuurmuseum bezocht.
          </p>
        </div>

        <div className="right">
          {toLeadImage("2021_doortrappers")} {toLeadImage("2021_doortrappers_2")}
        </div>

        <div className="subbox">
          <div className="row">
            <h2>Statistieken</h2>
            <h3>Geplande route</h3>
            {stats(38, 64, 41, 9, 42)}
            {toLeadImage("2021_hoogteprofiel")}
            TODO: gpx download
          </div>
        </div>
      </div>

      {/* --- 2022 --- */}
      <div className="box main">
        <div className="left">
          {title(2022, "Goor (NL) - Mander (NL)")}
          {participants(6, "Paul K (Atarimeister), Dennis (Schwitzi), Onze Henk (Pantalonmeister), Ivo (Fahrradführer), Roel (Dropmeister) en Mathijs (Frietmeister)")}
          <p>
            Ook dit jaar werd er een nieuwe Doortrapper verwelkomt; Roel alias 'Dropmeister'. Het werd al snel duidelijk dat hij beschikt over de nodige creativiteit. Zo had hij
            een lege doos Jodekoeken omgetoverd tot bidonhouder en bestond zijn 'fietstas' uit een gewone rugtas die middels spanbanden op de bagagedrager gemonteerd was. Dat
            laatste leverde wel de nodige stops op, waarbij het geheel opnieuw vastgezet moest worden.
          </p>
          <p>
            Het was weer een 2-daagse fietstocht als vanouds, met een overnachting in een hotel in Isselburg. De overnachting en het diner aldaar was prima, alleen waren er
            buitengewoon veel wespen die ook interesse hadden in avondeten.
          </p>
        </div>

        <div className="right">
          {toLeadImage("2022_doortrappers")} {toLeadImage("2022_doortrappers_2")}
        </div>

        <div className="subbox">
          <div className="row">
            <h2>Statistieken</h2>
            <h3>Geplande route</h3>
            {stats(140, 152, 122, 9, 66)}
            {toLeadImage("2022_hoogteprofiel")}
            {gpxDownload("2022-nijmegen-gronau.gpx")}
            <h3>Gemeten route</h3>
            (Tijd en gem. snelheid zijn exclusief pauzes)
            {recordings([
              recording("Nijmegen - Isselburg", 66.56, 17.7, "3h:45m", 610, 599, "2022-08-04_Nijmegen-Isselburg.gpx"),
              recording("Isselburg - Gronau", 71.81, 18.1, "3h:57m", 767, 755, "2022-08-05_Isselburg-Gronau.gpx"),
              recording("Gronau - Bocholt", 59.21, 18.9, "3h:07m", 745, 747, "2022-08-12_Gronau-Bocholt.gpx"),
              recording("Bocholt - Nijmegen", 75.58, 18.3, "4h:07m", 484, 532, "2022-08-13_Bocholt-Nijmegen.gpx"),
            ])}
          </div>
        </div>
      </div>

      {/* --- 2024 --- */}
      <div className="box light">
        <div className="left">
          {title(2024, "Nijmegen (NL) - Érezée (BE)")}
          {participants(6, "Paul K (Atarimeister), Dennis (Schwitzi), Onze Henk (Pantalonmeister), Ivo (Fahrradführer) en Mathijs (Frietmeister)")}
          <p>
            Voor het eerste deden we dit jaar 3 dagen over de heenreis; van Eindhoven naar Kleine Brogel (met een overnachting in Het Oud Klooster), van Kleine Brogel naar Huy
            (overnachting in Château de la Neuville, ook wel Château Nucleaire genoemd) en van Huy naar het huisje in Érezée. De terugweg deden we in twee dagen, grotendeels de
            Vennbahn volgens helemaal van Trois Point tot in het centrum van Aken. Op de terugweg overnachtten we in Roetgen hotel Brunnenhof, ook wel het Bruine Hofje genoemd).
          </p>
          <p>
            Er werd weer geklaagd over de nodige Ivo-paadjes, maar dat viel natuurlijk allemaal reuze mee. Op de heenweg waren er wel al direct grote problemen op station
            Eindhoven, waar Dennis volgens de NS al uitgecheckt was, en dus niet nog eens mocht uitchecken. Ook kregen we direct na Dommelen een +5 aan de broek van Onze Henk,
            omdat er nog een brief gepost moest worden bij een lid van de postzegelvereniging en Onze Henk een postzegel uit wilde sparen.
          </p>
          <p>
            Op de terugweg begonnen de problemen direct na de lunch bij 't Beufke in Landgraaf. Het achterwiel van Ivo's fiets wilde niet echt meer ronddraaien en leek continu op
            de rem te staan. Een fietsenmaker was niet in de buurt dus werd er besloten gewoon door te fietsen, terwijl het gepiep van de achterrem steeds luider werd. Bij een
            pauze kon het euvel alsnog gerepareerd worden en de weg vervolgd.
          </p>
          <p>
            Dit jaar konden er twee nieuwe Doortrappers aan de selectie toegevoegd worden; Irving en Abel fietsten de derde dag van de heenweg mee, vanuit Huy naar Érezée. Er waren
            op zowel de heenweg als de terugweg de nodige hoogtemeters te overwinnen, met enkele zeer stijle klimmetjes. Sommigen moesten de stijlste klimmetjes lopen omdat het
            arsenaal aan versnellingen simpelweg niet afdoende was. Het maximale stijgingspercentage van 147,8% in het kaartje onder is overigens wel een tikje overdreven, maar 20%
            werd zeker gehaald.
          </p>
        </div>

        <div className="right">
          {toLeadImage("2024_doortrappers")} {toLeadImage("2024_doortrappers_2")} {toLeadImage("2024_doortrappers_3")}
        </div>

        <div className="subbox">
          <div className="row">
            <h2>Statistieken</h2>
            <h3>Geplande route</h3>
            {stats(200, 1385, 1113, 16, 339)}
            {toLeadImage("2024_hoogteprofiel")}
            {gpxDownload("2024_eindhoven-erezee.gpx")}
            <h3>Gemeten route</h3>
            (Tijd en gem. snelheid zijn exclusief pauzes)
            {recordings([
              recording("Dommelen - Kleine-Brogel", 27.3, 18.4, "1h:28m", 303, 269, "2024-07-24-Dommelen-KleineBrogel.gpx"),
              recording("Kleine-Brogel - Huy", 106.57, 17.2, "6h:11m", 1509, 1489, "2024-07-25_KleineBrogel-Huy.gpx"),
              recording("Huy - Érezée", 57.36, 15.6, "3h:40m", 2092, 1880, "2024-07-26_Huy-Erezee.gpx"),
              recording("Érezée - Roetgen", 96.24, 18.2, "5h:17m", 2284, 2175, "2024-08-02_Erezee-Roetgen.gpx"),
              recording("Roetgen - Roermond", 90.44, 19.3, "4h:40m", 1602, 1965, "2024-08-03_Roetgen-Roermond.gpx"),
            ])}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Doortrappers;
